import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { exportNominationsToExcel } from '../lib/api';

// Open-password the exported .xlsx is encrypted with (must match the backend
// constant NominationExcelExporter.OpenPassword). Not a secret — it's printed
// here so admins can open the downloaded file.
const EXPORT_PASSWORD = '!emap123!';

/**
 * Administration → Export to Excel (NOC-360). A single button that downloads a
 * password-protected .xlsx dump of all live entries (one row per entry, textual
 * fields only — no images). Gated to Admin + User in the router and nav.
 */
export function ExportExcel() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFile, setLastFile] = useState<string | null>(null);

  const handleExport = async () => {
    setDownloading(true);
    setError(null);
    try {
      const filename = await exportNominationsToExcel();
      setLastFile(filename);
    } catch (err) {
      console.error(err);
      setError('The export failed. Please try again, or contact support if it keeps happening.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack spacing={3} alignItems="center">
      <Typography variant="h3" component="h1" align="center">
        Export to Excel
      </Typography>

      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 720, width: '100%' }}>
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box
            sx={(t) => ({
              width: 64,
              height: 64,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,204,0,0.16)' : 'rgba(255,204,0,0.22)',
            })}
          >
            <DescriptionOutlinedIcon fontSize="large" sx={{ color: 'text.primary' }} />
          </Box>

          <Stack spacing={1}>
            <Typography variant="h6">Download all entries</Typography>
            <Typography variant="body2" color="text.secondary">
              This page allows you to download a .xlsx export of all entries on the admin portal. This
              file contains personally identifiable information — please be extremely careful with how
              you handle and share this file. If you would like any more columns adding please contact
              dominic.cotterill@nocrus.co.uk
            </Typography>
          </Stack>

          <Alert
            severity="info"
            icon={<LockOutlinedIcon fontSize="inherit" />}
            sx={{ width: '100%', textAlign: 'left' }}
          >
            The file is password-protected. To open it, enter the password:{' '}
            <Box component="code" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
              {EXPORT_PASSWORD}
            </Box>
          </Alert>

          <Button
            variant="contained"
            size="large"
            onClick={handleExport}
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
          >
            {downloading ? 'Preparing…' : 'Export to Excel'}
          </Button>

          {lastFile && !error && (
            <Alert severity="success" sx={{ width: '100%' }}>
              Downloaded <strong>{lastFile}</strong>. Check your browser's downloads. Opening it
              requires the password{' '}
              <Box component="code" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                {EXPORT_PASSWORD}
              </Box>
              .
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
